import { useState, type FormEvent } from 'react';
import { doc } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useDocRow } from '@/lib/useDocs';
import { messageForAuthError } from '@/lib/authErrors';
import { cn } from '@/lib/cn';
import type { Diagnostic } from '@/types/domain';
import { strings } from '@/strings';

const saveDiagnostic = callable<Record<string, unknown>, { candidateId: string; stage: string }>('saveDiagnostic');

/** The eight prose questions, in the order أسئلة التشخيص الكتابية asks them. */
const QUESTIONS = [
  { key: 'career', label: strings.diagnosticCareer, rows: 6 },
  { key: 'formativeResponsibilities', label: strings.diagnosticFormative, rows: 4 },
  { key: 'outsideSupervision', label: strings.diagnosticOutside, rows: 4 },
  { key: 'achievements', label: strings.diagnosticAchievements, rows: 4 },
  { key: 'teamRole', label: strings.diagnosticTeamRole, rows: 3 },
  { key: 'motivations', label: strings.diagnosticMotivations, rows: 4 },
  { key: 'nonprofitDriver', label: strings.diagnosticDriver, rows: 3 },
] as const;

const LEADERSHIP = [
  { value: 'decisive', label: strings.leadStyleDecisive },
  { value: 'inspiring', label: strings.leadStyleInspiring },
  { value: 'collaborative', label: strings.leadStyleCollaborative },
  { value: 'methodical', label: strings.leadStyleMethodical },
] as const;

const MAX_STYLES = 2;

type Answers = Record<string, string>;

/**
 * أسئلة التشخيص الكتابية — stage two of استمارة الترشيح → التشخيص → المقابلة.
 *
 * A written interview, not a survey, and the layout says so: one long column of
 * generous text areas rather than a grid of compact inputs. The instrument's
 * own introduction tells people it takes fifteen to twenty minutes and is best
 * done in one sitting, so the form does not pretend to be quick.
 *
 * Someone who has already answered sees what they wrote, not an empty form:
 * re-submitting is allowed — the server keeps the superseded version — but
 * being shown a blank page after answering reads as if the answers were lost.
 */
export function DiagnosticPage() {
  const { claims } = useAuth();
  const candidateId = String(claims.candidateId || '');

  const { row, loading, error } = useDocRow<Diagnostic>(
    candidateId ? doc(db, 'candidates', candidateId, 'diagnostic', 'current') : null,
    [candidateId]
  );

  const [draft, setDraft] = useState<Answers | null>(null);
  const [styles, setStyles] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // The stored answers are the starting point until the person edits one.
  const answers: Answers = draft ?? {
    ...Object.fromEntries(QUESTIONS.map((q) => [q.key, (row as unknown as Answers)?.[q.key] || ''])),
    anythingElse: row?.anythingElse || '',
  };
  const chosen = styles ?? row?.leadershipStyles ?? [];

  const complete = QUESTIONS.every((q) => (answers[q.key] || '').trim()) && chosen.length > 0;

  function setAnswer(key: string, value: string) {
    setDraft({ ...answers, [key]: value });
    setSaved(false);
  }

  function toggleStyle(value: string) {
    const next = chosen.includes(value)
      ? chosen.filter((s) => s !== value)
      : [...chosen, value];
    // The cap is the consultant's, so it is enforced here rather than letting
    // the server refuse a form someone has just spent twenty minutes on.
    if (next.length > MAX_STYLES) {
      setProblem(strings.errTooManyStyles);
      return;
    }
    setProblem(null);
    setStyles(next);
    setSaved(false);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy || !complete) return;
    setBusy(true);
    setProblem(null);
    try {
      await saveDiagnostic({ candidateId, ...answers, leadershipStyles: chosen });
      setSaved(true);
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  if (!candidateId) return <p className="text-muted">{strings.loadFailed}</p>;

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h1 className="text-xl font-bold text-primary">{strings.diagnosticTitle}</h1>
        <p className="text-sm leading-relaxed text-muted">{strings.diagnosticIntro}</p>
      </header>

      {loading ? <p className="text-muted">{strings.loading}</p> : null}
      {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
      {row && !saved ? <p className="text-sm text-success">{strings.diagnosticDone}</p> : null}

      <form onSubmit={onSubmit} className="flex flex-col gap-6">
        {QUESTIONS.map((q) => (
          <label key={q.key} className="flex flex-col gap-2 text-sm" htmlFor={q.key}>
            <span className="font-medium leading-relaxed text-ink">{q.label}</span>
            <textarea
              id={q.key}
              rows={q.rows}
              value={answers[q.key] || ''}
              onChange={(e) => setAnswer(q.key, e.target.value)}
              className="rounded-md border border-border-subtle px-3 py-2 leading-relaxed"
            />
          </label>
        ))}

        <fieldset className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4">
          <legend className="px-1 text-sm font-medium text-ink">{strings.diagnosticLeadership}</legend>
          <p className="text-xs text-muted">{strings.diagnosticLeadershipHint}</p>
          {LEADERSHIP.map((style) => (
            <label
              key={style.value}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-md p-3 text-sm leading-relaxed transition-colors',
                chosen.includes(style.value) ? 'bg-surface text-ink' : 'text-ink hover:bg-surface'
              )}
            >
              <input
                type="checkbox"
                className="mt-1 shrink-0"
                checked={chosen.includes(style.value)}
                onChange={() => toggleStyle(style.value)}
              />
              <span>{style.label}</span>
            </label>
          ))}
        </fieldset>

        <label className="flex flex-col gap-2 text-sm" htmlFor="anythingElse">
          <span className="font-medium leading-relaxed text-ink">{strings.diagnosticAnythingElse}</span>
          <textarea
            id="anythingElse"
            rows={3}
            value={answers.anythingElse || ''}
            onChange={(e) => setAnswer('anythingElse', e.target.value)}
            className="rounded-md border border-border-subtle px-3 py-2 leading-relaxed"
          />
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={busy || !complete}
            className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {strings.diagnosticSubmit}
          </button>
          {!complete ? <span className="text-sm text-muted">{strings.errDiagnosticIncomplete}</span> : null}
          {saved ? <span className="text-sm text-success">{strings.diagnosticSaved}</span> : null}
          {problem ? <span role="alert" className="text-sm text-danger">{problem}</span> : null}
        </div>
      </form>
    </div>
  );
}
